import React from 'react';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { ThemeContext } from '../../../contexts/ThemeContext';
import {
  MARK_VIEWBOX,
  RING_PATH,
  RING_STROKE_WIDTH,
  STAR_PATH,
  STAR_TRANSFORM,
  STAR_SCALE,
  WORDMARK_VIEWBOX,
  WORDMARK_O_TRANSFORM,
  WORDMARK_N_TRANSFORM,
  WORDMARK_S_TRANSFORM,
  P_PATH,
  N_PATH,
  S_PATH,
} from './markGeometry';

export interface LogoProps {
  /** 'mark' = icon only (ring + star); 'wordmark' = full PONS where the O is the mark */
  variant?: 'mark' | 'wordmark';
  /** Size/color via Tailwind classes. Color inherits currentColor (defaults to text-fg-strong). */
  className?: string;
  /**
   * Set false for "provided by PONS" attribution icons that must show the
   * PONS mark even when the org has a white-label logo configured.
   */
  whiteLabelAware?: boolean;
}

/**
 * The one PONS logo component. Theme-aware via currentColor (ink on
 * light, paper on dark through text-fg-strong); the star is always
 * champagne. When an org has white-label enabled with a custom logo,
 * that logo renders instead - never hardcode /logosvg.svg or the brand
 * SVGs directly in feature code.
 */
const Logo: React.FC<LogoProps> = ({ variant = 'mark', className, whiteLabelAware = true }) => {
  const { t } = useTranslation();
  const { theme } = useContext(ThemeContext);
  const preferences = useSelector((state: RootState) => state.lists.preferences);
  const whiteLabelSettings = useSelector((state: RootState) => state.whiteLabel.settings);

  const isWhiteLabelEnabled =
    preferences?.WhiteLableEnabled === true || preferences?.WhiteLableEnabled === '1';
  const whiteLabelLogo =
    whiteLabelAware && isWhiteLabelEnabled
      ? theme === 'dark'
        ? whiteLabelSettings?.SidePanelLogoDark
        : whiteLabelSettings?.SidePanelLogoLight
      : null;

  const sizeClass = className || (variant === 'wordmark' ? 'h-6 w-auto' : 'w-6 h-6');

  if (whiteLabelLogo) {
    return (
      <img
        src={whiteLabelLogo}
        alt={t('Logo')}
        className={`${sizeClass} object-contain`}
      />
    );
  }

  if (variant === 'wordmark') {
    return (
      <svg
        viewBox={WORDMARK_VIEWBOX}
        className={`${sizeClass} text-fg-strong`}
        role="img"
        aria-label="PONS"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d={P_PATH} fill="currentColor" />
        <g transform={WORDMARK_O_TRANSFORM}>
          <path
            d={RING_PATH}
            fill="none"
            stroke="currentColor"
            strokeWidth={RING_STROKE_WIDTH}
            strokeLinecap="round"
          />
          <g transform={STAR_TRANSFORM}>
            <path d={STAR_PATH} transform={STAR_SCALE} fill="rgb(var(--champagne))" />
          </g>
        </g>
        <g transform={WORDMARK_N_TRANSFORM}>
          <path d={N_PATH} fill="currentColor" />
        </g>
        <g transform={WORDMARK_S_TRANSFORM}>
          <path d={S_PATH} fill="currentColor" />
        </g>
      </svg>
    );
  }

  return (
    <svg
      viewBox={MARK_VIEWBOX}
      className={`${sizeClass} text-fg-strong`}
      role="img"
      aria-label="PONS"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d={RING_PATH}
        fill="none"
        stroke="currentColor"
        strokeWidth={RING_STROKE_WIDTH}
        strokeLinecap="round"
      />
      <g transform={STAR_TRANSFORM}>
        <path d={STAR_PATH} transform={STAR_SCALE} fill="rgb(var(--champagne))" />
      </g>
    </svg>
  );
};

export default Logo;
